// src/views/pages/pos/components/reservations/ReservationDetailsDrawer.jsx
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Tag,
  Button,
  Modal,
  Typography,
  Space,
  message,
  Divider,
  Dropdown,
  Menu,
} from "antd";
import {
  EditOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  CopyOutlined,
  
} from "@ant-design/icons";
import moment from "moment";
import {
  UserPlus,
  Calendar,
  Clock,
  Users,
  MapPin,
  Hash,
  Phone,
  Mail,
  FileText,   MoreVertical as MoreOutlined,
} from "react-feather";

const { Title, Text } = Typography;

const KV = ({ icon, label, value, onCopy, copyLabel }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      padding: "12px 12px",
      borderRadius: 14,
      border: "1px solid #eef2f7",
      background: "#fff",
      alignItems: "flex-start",
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 12,
        display: "grid",
        placeItems: "center",
        background: "#f3f4f6",
        flex: "0 0 auto",
      }}
    >
      {icon}
    </div>

    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", letterSpacing: 0.2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#111827", marginTop: 2, wordBreak: "break-word" }}>
        {value || "-"}
      </div>
    </div>

    {onCopy && value ? (
      <Button
        size="small"
        icon={<CopyOutlined />}
        onClick={onCopy}
        aria-label={`Copy ${copyLabel || label}`}
        style={{ borderRadius: 10 }}
      />
    ) : null}
  </div>
);

KV.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string,
  value: PropTypes.any,
  onCopy: PropTypes.func,
  copyLabel: PropTypes.string,
};

export default function ReservationDetailsDrawer({
  open,
  reservation,
  tables = [],
  timezone,
  statusTheme = {},
  onClose,
  onEdit,
  onCancel,
  onDelete,
  onAddToQueue,
  queueLoading = false,

  // Optional future: queuedEntry to disable button (safe if not passed)
  queuedEntry,
}) {
  const tableLabel = useMemo(() => {
    if (!reservation) return "-";
    const tableId = reservation.tableId || reservation.assigned_table_id;
    if (!tableId || tableId === "unassigned") return "Unassigned";
    return tables.find((t) => String(t.id) === String(tableId))?.label || String(tableId);
  }, [reservation, tables]);

  const start = reservation?.start ? moment(reservation.start).tz(timezone) : null;
  const end = reservation?.end ? moment(reservation.end).tz(timezone) : null;

  const statusChip = useMemo(() => {
    const s = reservation?.status;
    if (!s) return null;
    const key = String(s);
    const theme = statusTheme?.[key] || {};
    return (
      <Tag
        style={{
          borderRadius: 999,
          fontWeight: 900,
          padding: "4px 12px",
          fontSize: 12,
          border: `1px solid ${theme.border || "#e5e7eb"}`,
          background: theme.bg || "#f3f4f6",
          color: theme.text || "#111827",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {key}
      </Tag>
    );
  }, [reservation, statusTheme]);

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(String(text));
      message.success(`${label} copied`);
    } catch {
      message.error(`Failed to copy ${label}`);
    }
  };

  const confirmAction = (action, title, content) => {
    Modal.confirm({
      title,
      content,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => action?.(reservation),
    });
  };

  const statusLower = String(reservation?.status || "").toLowerCase();
  const canQueue =
    !!reservation &&
    statusLower !== "cancelled" &&
    statusLower !== "canceled" &&
    statusLower !== "deleted" &&
    statusLower !== "no_show" &&
    statusLower !== "no-show";

  const moreMenu = (
    <Menu
      items={[
        {
          key: "cancel",
          icon: <CloseCircleOutlined />,
          label: "Cancel Reservation",
          danger: true,
          onClick: () =>
            confirmAction(onCancel, "Cancel reservation?", "Are you sure you want to cancel this reservation?"),
          disabled: !reservation,
        },
        {
          key: "delete",
          icon: <DeleteOutlined />,
          label: "Delete Reservation",
          danger: true,
          onClick: () =>
            confirmAction(onDelete, "Delete reservation?", "Are you sure you want to delete this reservation?"),
          disabled: !reservation,
        },
      ]}
    />
  );

  // Tight top summary chips
  const summaryChips = (
    <Space wrap size={8}>
      {statusChip}
      <Tag style={{ borderRadius: 999, fontWeight: 800, background: "#fff", border: "1px solid #eef2f7" }}>
        <Calendar size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
        {start ? start.format("ddd, DD MMM YYYY") : "-"}
      </Tag>
      <Tag style={{ borderRadius: 999, fontWeight: 800, background: "#fff", border: "1px solid #eef2f7" }}>
        <Clock size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
        {start ? start.format("h:mm a") : "-"}
        {end ? ` – ${end.format("h:mm a")}` : ""}
      </Tag>
      <Tag style={{ borderRadius: 999, fontWeight: 800, background: "#fff", border: "1px solid #eef2f7" }}>
        <Users size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
        {reservation?.partySize ?? reservation?.party_size ?? "-"} guests
      </Tag>
      <Tag style={{ borderRadius: 999, fontWeight: 800, background: "#fff", border: "1px solid #eef2f7" }}>
        <MapPin size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
        {tableLabel}
      </Tag>
    </Space>
  );

  return (
    <Drawer
      placement="right"
      open={open}
      onClose={onClose}
      width={600}
      zIndex={2147483000}
      rootStyle={{ position: "fixed" }}
      mask
      className="ns-rdd-drawer"
      maskStyle={{ background: "rgba(15, 23, 42, 0.08)", backdropFilter: "blur(1.5px)" }}
      bodyStyle={{ background: "#f8fafc", padding: 18 }}
      headerStyle={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
        borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        padding: "16px 18px",
      }}
      title={
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <Title level={4} style={{ margin: 0, fontWeight: 900, lineHeight: 1.2 }}>
              Reservation
            </Title>
            <div style={{ marginTop: 8 }}>{summaryChips}</div>
            <div style={{ marginTop: 10, color: "#6b7280", fontSize: 12 }}>
              Times shown in <b>{timezone}</b>
            </div>
          </div>

          <Space align="start" style={{ flex: "0 0 auto" }}>
            <Button
              icon={<UserPlus size={16} />}
              onClick={() => {
                if (queuedEntry) return message.info("Already in queue");
                onAddToQueue?.(reservation);
              }}
              disabled={!canQueue || !onAddToQueue || !!queuedEntry}
              loading={queueLoading}
              style={{ borderRadius: 12 }}
            >
              {queuedEntry ? "In Queue" : "Add to Queue"}
            </Button>

            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEdit?.(reservation)}
              disabled={!reservation}
              style={{ borderRadius: 12 }}
            >
              Edit
            </Button>

            <Dropdown overlay={moreMenu} trigger={["click"]} placement="bottomRight">
              <Button icon={<MoreOutlined />} style={{ borderRadius: 12 }} />
            </Dropdown>

            <Button
            aria-label="Close"
            onClick={onClose}
            icon={<CloseCircleOutlined />}
            style={{ borderRadius: 12 }}
          />
          </Space>
        </div>
      }
    >
      <style>{`
        .ns-rdd-drawer .ant-drawer-content-wrapper {
          box-shadow: -18px 0 52px rgba(15, 23, 42, 0.16) !important;
        }

        .ns-rdd-drawer .ant-drawer-content {
          background: #f8fafc !important;
        }

        .ns-rdd-drawer .ant-drawer-header {
          position: sticky;
          top: 0;
          z-index: 2;
        }

        .ns-rdd-drawer .ant-drawer-title h4,
        .ns-rdd-drawer .ant-typography {
          color: #001f5b;
        }

        .ns-rdd-drawer .ant-drawer-body {
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .ns-rdd-drawer .ant-btn {
          border-radius: 13px !important;
          font-weight: 900;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
        }

        .ns-rdd-drawer .ant-btn-primary {
          background: linear-gradient(135deg, #1d4ed8, #2563eb) !important;
          border-color: #1d4ed8 !important;
          box-shadow: 0 12px 26px rgba(37, 99, 235, 0.18);
        }

        .ns-rdd-drawer .ant-tag {
          min-height: 30px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border-radius: 999px !important;
          font-weight: 900 !important;
          margin-inline-end: 0 !important;
        }

        .ns-rdd-drawer .ant-divider {
          border-color: rgba(15, 23, 42, 0.08);
        }

        @media (max-width: 680px) {
          .ns-rdd-drawer .ant-drawer-content-wrapper {
            width: 100vw !important;
          }
        }
      `}</style>

      {!reservation ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid #eef2f7",
            borderRadius: 16,
            padding: 18,
          }}
        >
          <Text type="secondary" style={{ fontSize: 14 }}>
            No reservation selected.
          </Text>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {/* Main Info */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #eef2f7",
              borderRadius: 16,
              padding: 16,
              boxShadow: "0 10px 30px rgba(17,24,39,.04)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Text style={{ fontWeight: 900, color: "#111827" }}>Guest</Text>
              {reservation?.id ? (
                <Tag style={{ borderRadius: 999, fontWeight: 900, background: "#f3f4f6", border: "1px solid #e5e7eb" }}>
                  <Hash size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                  #{reservation.id}
                </Tag>
              ) : null}
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <KV
                icon={<Users size={16} />}
                label="Guest Name"
                value={reservation.customerName || reservation.guest_name || reservation.name || "-"}
              />

              <KV icon={<MapPin size={16} />} label="Source" value={reservation.source || reservation.reservation_source || "-"} />

              <KV
                icon={<Phone size={16} />}
                label="Phone"
                value={reservation.phone || reservation.contact_number || "-"}
                onCopy={
                  reservation.phone || reservation.contact_number
                    ? () => handleCopy(reservation.phone || reservation.contact_number, "Phone")
                    : null
                }
                copyLabel="Phone"
              />

              <KV
                icon={<Mail size={16} />}
                label="Email"
                value={reservation.email || "-"}
                onCopy={reservation.email ? () => handleCopy(reservation.email, "Email") : null}
                copyLabel="Email"
              />
            </div>
          </div>

          {/* Timing & Notes */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #eef2f7",
              borderRadius: 16,
              padding: 16,
              boxShadow: "0 10px 30px rgba(17,24,39,.04)",
            }}
          >
            <Text style={{ fontWeight: 900, color: "#111827" }}>Reservation</Text>
            <Divider style={{ margin: "12px 0" }} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <KV
                icon={<Calendar size={16} />}
                label="Date"
                value={start ? start.format("dddd, DD MMM YYYY") : "-"}
              />
              <KV
                icon={<Clock size={16} />}
                label="Time"
                value={
                  start
                    ? `${start.format("h:mm a")}${end ? ` – ${end.format("h:mm a")}` : ""}`
                    : "-"
                }
              />
              <KV
                icon={<Users size={16} />}
                label="Party Size"
                value={`${reservation.partySize ?? reservation.party_size ?? "-"} guests`}
              />
              <KV icon={<MapPin size={16} />} label="Table" value={tableLabel} />
            </div>

            {reservation.notes || reservation.special_requests ? (
              <>
                <Divider style={{ margin: "14px 0" }} />
                <KV
                  icon={<FileText size={16} />}
                  label="Notes"
                  value={reservation.notes || reservation.special_requests}
                />
              </>
            ) : null}
          </div>
        </div>
      )}
    </Drawer>
  );
}

ReservationDetailsDrawer.propTypes = {
  open: PropTypes.bool,
  reservation: PropTypes.object,
  tables: PropTypes.array,
  timezone: PropTypes.string,
  statusTheme: PropTypes.object,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  onAddToQueue: PropTypes.func,
  queueLoading: PropTypes.bool,
  queuedEntry: PropTypes.object,
};
